import { type ReactNode, useEffect, useRef } from 'react';
import { Box, Button, IconButton, Text } from '@zillow/constellation-10';
import { IconEditFormFilled } from '@zillow/constellation-icons';
import { format } from 'date-fns';
import type { LocalDate, RenterInfoRenterProfileStandalonePageWebApiResponse } from '@zg-rentals/ts-renter-info-client';
import {
  CREDIT_SCORE_CHIP_OPTIONS,
  formatPhoneNumberInput,
  LEASE_DURATION_OPTIONS,
  NUM_HOUSEHOLD_OPTIONS,
  PET_OPTIONS,
} from '@zg-rentals/renter-profile-components';

type ViewRenterProfileContentProps = {
  profile: RenterInfoRenterProfileStandalonePageWebApiResponse;
  onEdit: () => void;
  /** When true, scroll this view into view on mount (e.g. after a save reverts to read mode). */
  scrollOnMount?: boolean;
};

const EMPTY = '';
const NOT_PROVIDED = 'Not provided';

const BEDROOM_LABELS: Record<string, string> = {
  STUDIO: 'Studio',
  '1_BD': '1 bed',
  '2_BDS': '2 bed',
  '3_BDS': '3 bed',
  '4_PLUS_BDS': '4+ beds',
};

const getLabel = (options: ReadonlyArray<{ value: string; label: string }>, value?: string): string =>
  options.find((option) => option.value === value)?.label ?? EMPTY;

const formatMoveInDate = (date?: LocalDate): string =>
  date ? format(new Date(date.year, date.month - 1, date.day), 'MM/dd/yyyy') : EMPTY;

const formatAnnualIncome = (annual?: number): string =>
  annual !== undefined ? `$${annual.toLocaleString('en-US')} annually` : EMPTY;

const formatMonthlyVoucher = (monthly?: number): string =>
  monthly !== undefined ? `$${monthly.toLocaleString('en-US')}/month` : EMPTY;

const formatPhone = (phone?: string): string => (phone ? formatPhoneNumberInput(phone) : EMPTY);

// Optional fields that are left unanswered render "Not provided" in subtle grey so a blank line
// doesn't read as missing/ambiguous to rental managers viewing the profile.
const ProfileField = ({ label, value, optional = false }: { label: string; value: string; optional?: boolean }) => {
  const showNotProvided = optional && value === EMPTY;
  return (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tightest', overflowWrap: 'break-word' }}>
      <Text textStyle="body-sm-bold">{label}</Text>
      <Text
        textStyle="body"
        data-testid="profile-field-value"
        css={showNotProvided ? { color: 'text.subtle' } : undefined}
      >
        {showNotProvided ? NOT_PROVIDED : value}
      </Text>
    </Box>
  );
};

const FieldRow = ({ left, right }: { left: ReactNode; right: ReactNode }) => (
  <Box
    css={{
      display: 'flex',
      flexDirection: { base: 'column', lg: 'row' },
      gap: { base: 'looser', lg: 'default' },
      width: '100%',
    }}
  >
    <Box css={{ flex: 1, minWidth: 0 }}>{left}</Box>
    <Box css={{ flex: 1, minWidth: 0 }}>{right}</Box>
  </Box>
);

export const ViewRenterProfileContent = ({ profile, onEdit, scrollOnMount = false }: ViewRenterProfileContentProps) => {
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollOnMount) {
      rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [scrollOnMount]);

  return (
    <Box
      ref={rootRef}
      data-testid="view-renter-profile-content"
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'loose',
        backgroundColor: 'bg.neutral',
        borderRadius: 'obj.default',
        paddingBlock: 'loose',
        paddingInline: 'loose',
      }}
    >
      <Box css={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tightest' }}>
          <Text textStyle="heading-xs">
            We&rsquo;ll include any details you share here with rental managers you contact.
          </Text>
          <Text textStyle="body" css={{ color: 'text.subtle' }}>
            This helps complete the picture of you as a renter.
          </Text>
        </Box>
        <IconButton
          data-testid="edit-profile-icon-button"
          emphasis="outlined"
          shape="square"
          size="sm"
          title="Edit profile"
          icon={<IconEditFormFilled />}
          tone="brand"
          onClick={onEdit}
          css={{ display: { base: 'none', lg: 'inline-flex' } }}
        />
      </Box>

      <Box css={{ display: 'flex', flexDirection: 'column', gap: 'looser', width: '100%' }}>
        <FieldRow
          left={<ProfileField label="First name" value={profile.firstName ?? EMPTY} />}
          right={<ProfileField label="Last name" value={profile.lastName ?? EMPTY} />}
        />
        <FieldRow
          left={<ProfileField label="Email" value={profile.email ?? EMPTY} />}
          right={<ProfileField label="Phone number" value={formatPhone(profile.phoneNumber)} />}
        />
        <FieldRow
          left={
            <ProfileField
              label="Preferred move-in date"
              value={formatMoveInDate(profile.preferredMoveInDate)}
              optional
            />
          }
          right={
            <ProfileField
              label="Total people in household"
              value={getLabel(NUM_HOUSEHOLD_OPTIONS, profile.totalPeopleInHousehold)}
              optional
            />
          }
        />
        <FieldRow
          left={
            <ProfileField
              label="Estimated household income"
              value={formatAnnualIncome(profile.estimatedHouseholdIncome)}
              optional
            />
          }
          right={
            <ProfileField label="Voucher amount" value={formatMonthlyVoucher(profile.voucherAmountMonthly)} optional />
          }
        />
        <ProfileField label="Pets" value={getLabel(PET_OPTIONS, profile.pets)} optional />
        <ProfileField label="Credit score" value={getLabel(CREDIT_SCORE_CHIP_OPTIONS, profile.creditScore)} optional />
        <ProfileField
          label="Preferred lease duration"
          value={getLabel(LEASE_DURATION_OPTIONS, profile.preferredLeaseDuration)}
          optional
        />
        <ProfileField
          label="Preferred bedroom count"
          value={profile.preferredBedroomCount ? (BEDROOM_LABELS[profile.preferredBedroomCount] ?? EMPTY) : EMPTY}
          optional
        />
        <ProfileField
          label="Parking needed"
          value={profile.isParkingNeeded === undefined ? EMPTY : profile.isParkingNeeded ? 'Yes' : 'No'}
          optional
        />
      </Box>

      <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)' }}>
        <Box css={{ gridColumn: { base: 'span 12', lg: 'span 8' } }}>
          <Button data-testid="edit-profile-button" emphasis="outlined" fluid onClick={onEdit}>
            Edit
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
